import React from "react";
import {
  Box,
  Button,
  Card,
  Chip,
  Typography,
} from "@mui/material";
import { FaChartLine } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import colors from "../styles/colors";
import { COLORS } from "../styles/uiConstants";
import EmptyState from "../components/atoms/EmptyState";
import {
  categoryChipSx,
  reportTemplates,
} from "../utils/reportTemplates";

export default function Reports() {
  const navigate = useNavigate();

  const goReportDetail = (template) => {
    navigate(`/Raporlar/${template.id}`);
  };

  return (
    <Box
      sx={{
        height: "100%",
        background: `linear-gradient(135deg, ${COLORS.slate50} 0%, ${COLORS.slate200} 100%)`,
        p: { xs: 2, sm: 3, md: 4 },
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          gap: 2,
          height: "100%",
          minHeight: 0,
        }}
      >
        <Card
          sx={{
            borderRadius: 4,
            p: 3,
            background: `linear-gradient(135deg, ${COLORS.white} 0%, ${COLORS.slate50} 100%)`,
            boxShadow: "0 14px 40px rgba(15,23,42,0.08)",
            border: `1px solid ${COLORS.slate200}`,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, minWidth: 0 }}>
              <Box
                sx={{
                  width: 52,
                  height: 52,
                  borderRadius: 3,
                  background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryDark} 100%)`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "white",
                  flexShrink: 0,
                  boxShadow: "0 10px 24px rgba(37,99,235,0.35)",
                }}
              >
                <FaChartLine size={22} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="h5"
                  sx={{ fontWeight: 900, color: colors.primary, lineHeight: 1.2 }}
                >
                  Raporlar
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: "text.secondary", fontWeight: 600 }}
                >
                  Klinik ve sistem performansını özetleyen rapor şablonları.
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
              <Chip
                label="Bir rapor seçerek detaya geç"
                sx={{
                  bgcolor: "rgba(37,99,235,0.08)",
                  color: colors.primaryDark,
                  fontWeight: 700,
                  borderRadius: 2,
                }}
              />
            </Box>
          </Box>
        </Card>

        <Card
          sx={{
            borderRadius: 4,
            overflow: "hidden",
            boxShadow: "0 12px 32px rgba(15,23,42,0.08)",
            border: `1px solid ${COLORS.slate200}`,
            background: COLORS.white,
            display: "flex",
            flexDirection: "column",
            flex: 1,
            minHeight: 0,
          }}
        >
          <Box sx={{ flex: 1, minHeight: 0, position: "relative", display: "flex", flexDirection: "column" }}>
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                overflow: "auto",
                p: 2,
              }}
            >
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    md: "repeat(2, minmax(0, 1fr))",
                    xl: "repeat(3, minmax(0, 1fr))",
                  },
                  gap: 2,
                }}
              >
                {reportTemplates.map((template) => (
                  <Card
                    key={template.id}
                    onClick={() => goReportDetail(template)}
                    sx={{
                      p: 2.5,
                      borderRadius: 3,
                      border: `1px solid ${COLORS.slate200}`,
                      boxShadow: "0 6px 18px rgba(15,23,42,0.06)",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        transform: "translateY(-2px)",
                        boxShadow: "0 12px 28px rgba(15,23,42,0.12)",
                        borderColor: "rgba(37,99,235,0.35)",
                      },
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: 2,
                          background: "rgba(37,99,235,0.08)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: colors.primary,
                          flexShrink: 0,
                        }}
                      >
                        <FaChartLine size={18} />
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 900, color: "text.primary" }}>
                          {template.name}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: "text.secondary", fontWeight: 600 }}
                        >
                          {template.description}
                        </Typography>
                      </Box>
                    </Box>

                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 2 }}>
                      <Chip
                        size="small"
                        label={template.category}
                        sx={{ ...categoryChipSx(template.category), borderRadius: 2, px: 1 }}
                      />
                      <Chip
                        size="small"
                        label={template.scope === "system" ? "Sistem" : "Klinik"}
                        sx={{
                          bgcolor: template.scope === "system" ? COLORS.catBlueLight : COLORS.successLight,
                          color: template.scope === "system" ? COLORS.catBlueDark : COLORS.successDarker,
                          fontWeight: 700,
                          borderRadius: 2,
                          px: 1,
                        }}
                      />
                      <Chip
                        size="small"
                        label={template.defaultPeriod}
                        sx={{
                          bgcolor: COLORS.slate100,
                          color: COLORS.slate700,
                          fontWeight: 700,
                          borderRadius: 2,
                          px: 1,
                        }}
                      />
                    </Box>

                    <Box sx={{ mt: 2 }}>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={(e) => {
                          e.stopPropagation();
                          goReportDetail(template);
                        }}
                        sx={{
                          borderRadius: 2,
                          fontWeight: 800,
                          textTransform: "none",
                          borderColor: COLORS.slate200,
                          "&:hover": {
                            borderColor: colors.primary,
                            bgcolor: "rgba(37,99,235,0.06)",
                          },
                        }}
                      >
                        Detaya Git
                      </Button>
                    </Box>
                  </Card>
                ))}
              </Box>
            </Box>

            {reportTemplates.length === 0 ? (
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  pointerEvents: "none",
                  p: 2,
                }}
              >
                <EmptyState
                  icon={<FaChartLine />}
                  title="Sonuç bulunamadı"
                  description="Şu anda gösterilecek rapor bulunmuyor."
                />
              </Box>
            ) : null}
          </Box>
        </Card>
      </Box>
    </Box>
  );
}
